import { withTransaction } from '@elastic/apm-rum-react'
import { Campaign, InfinityScroll, Media, UserInDetails, VisionService } from '@hub/shared'
import { SqLoader } from '@squidit/react-css'
import MediaApprovalCardComponent from '@src/components/media-approval-card/media-approval-card.component'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CardToUploadContent from './components/card-to-upload-content/card-to-upload-content.component'
import CardToUploadVideo from './components/card-to-upload-video/card-to-upload-video.component'
import MainContent from './components/main-content/main-content.component'
import './publish.page.scoped.scss'

const MEDIAS_PER_PAGE = 5

interface PostPublications {
  data: Media[]
  total: number
}

interface Props {
  campaign: Campaign
  userInCampaign: UserInDetails
}

const PublishPageComponent = ({ campaign, userInCampaign }: Props) => {
  const visionService = useMemo(() => new VisionService(), [])

  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingPagination, setIsLoadingPagination] = useState(false)
  const [postPublications, setPostPublications] = useState<PostPublications>({ data: [], total: 0 })
  const [nextPage, setNextPage] = useState(0)

  const canShowButtonContent = useMemo(() => ['instagram'].includes(campaign?.socialNetworks?.[0]), [campaign])
  const canShowButtonYoutube = useMemo(() => ['youtube'].includes(campaign?.socialNetworks[0]), [campaign])

  const [t, i18n] = useTranslation('publishPage')

  let startDate = useMemo(() => {
    return campaign?.startDate ?? `${new Date(campaign?.activation?.startDate).toISOString().split('T')[0]}T00:00:00-03:00`
  }, [campaign?.activation?.startDate, campaign?.startDate])

  let endDate = useMemo(() => {
    return campaign?.endDate ?? `${new Date().toISOString().split('T')[0]}T23:59:59-03:00`
  }, [campaign?.endDate])

  const timezone = useMemo(() => campaign?.timezone || 'America/Sao_Paulo', [campaign?.timezone])

  const fetchPosts = useCallback(async (): Promise<void> => {
    setIsLoadingPagination(true)
    visionService
      .getCampaignUserMedias({
        socialId: userInCampaign?.idNetwork,
        campaignId: campaign?._id,
        socialNetwork: campaign?.socialNetworks[0] as 'instagram' | 'tiktok' | 'twitter' | 'youtube',
        per_page: MEDIAS_PER_PAGE,
        page: nextPage,
        sort_by: 'criadoEm',
        order_by: '-1',
        status: ['approved', 'none'],
        start_date: startDate,
        end_date: endDate,
        timezone,
      })
      .then((newPosts: PostPublications) => {
        const newArray = postPublications?.data?.concat(newPosts?.data || [])
        setPostPublications((old) => ({ ...old, data: newArray, total: newPosts?.total }))
        setNextPage(newPosts?.total > newArray?.length ? nextPage + 1 : 0)
      })
      .finally(() => {
        setIsLoading(false)
        setIsLoadingPagination(false)
      })
  }, [
    campaign?._id,
    campaign?.socialNetworks,
    nextPage,
    userInCampaign?.idNetwork,
    visionService,
    postPublications?.data,
    startDate,
    endDate,
    timezone,
  ])

  const onLoadMore = useCallback(() => {
    if (!isLoadingPagination) {
      fetchPosts()
    }
  }, [fetchPosts, isLoadingPagination])

  useEffect(() => {
    fetchPosts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (isLoading) {
    return (
      <div className="text-center py-3" style={{ width: '100%' }}>
        <SqLoader size="big" />
      </div>
    )
  }

  return (
    <div>
      {canShowButtonContent && <CardToUploadContent campaign={campaign} userInCampaign={userInCampaign} />}
      {canShowButtonYoutube && <CardToUploadVideo campaign={campaign} userInCampaign={userInCampaign} fetchPosts={fetchPosts} />}
      <hr />
      <MainContent>
        <MainContent.Heading>{t('yourPost')}</MainContent.Heading>
        <MainContent.UpdatedAt>{`${t('updatedAt')} - ${new Date().toLocaleDateString(i18n.language)}`}</MainContent.UpdatedAt>
        {!isLoading && postPublications?.data?.length > 0 ? (
          <div className="list-content">
            <InfinityScroll
              className="publish-page-infinity-scroll"
              loading={isLoadingPagination}
              onLoadMore={onLoadMore}
              hasMore={!!nextPage}
              endMessage={' '}
              elementToScrollId={innerWidth < 991 ? 'details-page' : null}
            >
              <ul>
                {postPublications?.data?.map((media) => (
                  <li key={media?._id}>
                    <MediaApprovalCardComponent
                      campaign={campaign}
                      media={media}
                      socialNetwork={campaign?.socialNetworks[0]}
                      userInCampaign={userInCampaign}
                    />
                  </li>
                ))}
              </ul>
            </InfinityScroll>
          </div>
        ) : null}
      </MainContent>
    </div>
  )
}

export default withTransaction('PublishPageComponent', 'component')(PublishPageComponent)
